import type { IsoDateTime } from './common';
import type { OrderId, PackingStationId, PrinterId, PrintJobId, TenantId } from './ids';

export const SHIPPING_MODELS = ['marketplace', 'seller_contract', 'warehouse_contract'] as const;
/**
 * Whose carrier contract ships an order (Modül C):
 * - marketplace: the channel's contracted carrier; label and tracking number come from the marketplace API
 * - seller_contract: the seller's own carrier account
 * - warehouse_contract: the 3PL's carrier account, re-billed to the seller
 */
export type ShippingModel = (typeof SHIPPING_MODELS)[number];

/** Known carriers autocomplete; codes of carriers added later are still accepted. */
export type CarrierCode =
  | 'trendyol_express'
  | 'hepsijet'
  | 'aras'
  | 'yurtici'
  | 'mng'
  | 'surat'
  | 'ptt'
  | 'ups'
  | 'kolay_gelsin'
  | 'amazon_shipping'
  | (string & {});

export const LABEL_FORMATS = ['ZPL', 'PDF', 'PNG'] as const;
export type LabelFormat = (typeof LABEL_FORMATS)[number];

/** Thermal label sizes in millimetres, plus A4 for documents that only exist as A4 PDFs. */
export const PAPER_SIZES = ['100x150', '100x100', 'A4'] as const;
export type PaperSize = (typeof PAPER_SIZES)[number];

export type DocumentPayload =
  /** Raw ZPL */
  | { readonly encoding: 'utf8'; readonly data: string }
  /** PDF / PNG as returned by a marketplace or integrator API */
  | { readonly encoding: 'base64'; readonly data: string }
  /** Archived in object storage; printers fetch it by key */
  | { readonly encoding: 'object'; readonly key: string; readonly sha256: string };

export interface ShippingLabel {
  readonly carrier: CarrierCode;
  readonly trackingNumber: string;
  readonly format: LabelFormat;
  readonly paper: PaperSize;
  readonly payload: DocumentPayload;
  readonly fetchedAt: IsoDateTime;
  /** Package the channel created while issuing the label (Hepsiburada packageNumber), if any */
  readonly externalPackageId?: string | null;
}

export const DOCUMENT_STATUSES = ['pending', 'ready', 'failed'] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

export const DOCUMENT_SOURCES = ['marketplace', 'carrier', 'warehouse', 'seller_upload', 'integrator'] as const;
export type DocumentSource = (typeof DOCUMENT_SOURCES)[number];

/** Document metadata (order_documents without the content). */
export interface OrderDocumentInfo {
  readonly id: string;
  readonly orderId: OrderId;
  readonly kind: DocumentKind;
  readonly status: DocumentStatus;
  readonly format: LabelFormat | null;
  readonly paper: PaperSize | null;
  readonly documentNumber: string | null;
  readonly documentUrl: string | null;
  readonly source: DocumentSource;
  readonly trackingNumber: string | null;
  readonly error: string | null;
  /** the content went with the order's personal data (KVKK): the row stays, nothing to open or print */
  readonly contentErasedAt: IsoDateTime | null;
  readonly updatedAt: IsoDateTime;
}

/**
 * e_waybill = the seller's e-İrsaliye and e_archive_invoice = the seller's e-Arşiv fatura, both issued by
 * the seller's own e-invoice integrator. The warehouse only invoices storage & handling to the seller.
 */
export const DOCUMENT_KINDS = ['shipping_label', 'e_waybill', 'e_archive_invoice', 'packing_slip'] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export const DOCUMENT_KIND_LABELS: Readonly<Record<DocumentKind, string>> = {
  shipping_label: 'Kargo etiketi',
  e_waybill: 'e-İrsaliye',
  e_archive_invoice: 'e-Arşiv fatura',
  packing_slip: 'Sevk listesi',
};

export const PRINT_JOB_STATUSES = ['pending', 'sent', 'printed', 'failed'] as const;
export type PrintJobStatus = (typeof PRINT_JOB_STATUSES)[number];

export interface PrintJob {
  readonly id: PrintJobId;
  readonly tenantId: TenantId;
  readonly orderId: OrderId;
  readonly stationId: PackingStationId;
  readonly printerId: PrinterId;
  readonly kind: DocumentKind;
  readonly format: LabelFormat;
  readonly paper: PaperSize;
  readonly copies: number;
  readonly payload: DocumentPayload;
}
